import * as React from 'react';
import { observer } from 'mobx-react-lite';
import FontPickerViewState from 'owa-editor-ribbon-secondary-ui/lib/store/schema/FontPickerViewState';
import setFontPickerDisplayValue from 'owa-editor-ribbon-secondary-ui/lib/actions/setFontPickerDisplayValue';

import styles from './PickerInput.scss';
import classNames from 'classnames';
const KEY_TAB = 9;
const KEY_ENTER = 13;
const KEY_ESC = 27;
const KEY_UP = 38;
const KEY_DOWN = 40;

export interface PickerInputHandle {
    focus: () => void;
}

export default observer(
    function PickerInput(
        {
            currentValue,
            className,
            onSubmit,
            width,
            maxLength,
            autoComplete,
            viewState,
            onCancel,
            inputStyle,
        }: {
            currentValue: string;
            width: number;
            maxLength: number;
            autoComplete: boolean;
            viewState: FontPickerViewState;
            inputStyle?: (displayValue: string) => React.CSSProperties;
            onSubmit?: (value: string) => void;
            onCancel?: () => void;
            className?: string;
        },
        ref: React.Ref<PickerInputHandle>
    ) {
        const input = React.useRef<HTMLInputElement>();
        const { displayValue } = viewState;
        const [typedValue, setTypedValue] = React.useState('');
        const valueList = viewState.items.map(item => item.displayValue);
        const currentDisplayValue =
            viewState.items.filter(
                item =>
                    currentValue && item.submitValue?.toLowerCase() == currentValue?.toLowerCase()
            )[0]?.displayValue || currentValue;
        const value = displayValue === null ? currentDisplayValue : displayValue;

        React.useImperativeHandle(
            ref,
            () => ({
                focus: () => {
                    input.current?.focus();
                },
            }),
            []
        );

        React.useEffect(() => {
            if (input.current?.ownerDocument?.activeElement == input.current) {
                const lowerCaseDisplayValue = displayValue?.toLowerCase() || '';
                const lowerCaseTypedValue = typedValue?.toLowerCase() || '';
                if (lowerCaseDisplayValue.indexOf(lowerCaseTypedValue) == 0) {
                    input.current.selectionStart = lowerCaseTypedValue.length;
                    input.current.selectionEnd = lowerCaseDisplayValue.length;
                }
            }
        }, [displayValue, typedValue]);

        const onFocus = React.useCallback(() => {
            setTypedValue('');
            setFontPickerDisplayValue(viewState, currentDisplayValue);
        }, [currentDisplayValue]);

        const onBlur = React.useCallback(() => {
            setTypedValue('');
            setFontPickerDisplayValue(viewState, null);
        }, []);

        const onChange = React.useCallback(() => {
            const newValue = input.current.value;

            if (newValue.length <= typedValue.length || !autoComplete) {
                setFontPickerDisplayValue(viewState, newValue);
            } else if (displayValue?.indexOf(newValue) !== 0) {
                const newValueInLowerCase = newValue.toLowerCase();
                const newDisplayValue = valueList.filter(
                    v => v.toLowerCase().indexOf(newValueInLowerCase) == 0
                )[0];
                setFontPickerDisplayValue(viewState, newDisplayValue || newValue);
            }

            setTypedValue(newValue);
        }, [typedValue, displayValue]);

        const onKeyDown = React.useCallback(
            (e: React.KeyboardEvent<EventTarget>) => {
                if (e.which == KEY_ENTER || e.which == KEY_TAB) {
                    e.preventDefault();
                    e.stopPropagation();
                    onSubmit?.(displayValue);
                } else if (e.which == KEY_ESC) {
                    e.preventDefault();
                    e.stopPropagation();
                    setFontPickerDisplayValue(viewState, null);
                    setTypedValue('');
                    onCancel?.();
                } else if (e.which == KEY_UP || e.which == KEY_DOWN) {
                    let index = valueList.indexOf(displayValue);

                    if (index >= 0) {
                        index += e.which == KEY_UP ? -1 : 1;
                        index = (index + valueList.length) % valueList.length;
                        setFontPickerDisplayValue(viewState, valueList[index]);
                        setTypedValue('');
                    }
                }
            },
            [displayValue]
        );

        return (
            <input
                type="text"
                className={classNames(styles.input, className)}
                style={{
                    ...(inputStyle?.(value) || {}),
                    width: width + 'px',
                }}
                maxLength={maxLength}
                ref={input}
                value={value}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={onChange}
                onKeyDown={onKeyDown}
            />
        );
    },
    {
        forwardRef: true,
    }
);
